import { tableHelper, fieldHelper, generateObjectId } from '@builttocreate/engine-utils';
import FieldTypes from './FieldTypes';
import collectionFieldSchemas from '../testUtils/collectionFieldSchemas';
import DefaultCollectionSchema from './DefaultCollectionSchema';

import FieldDisplayTypes from './FieldDisplayTypes';
import FormStylesOptions from './FormStylesOptions';

import ThemeIcons from '../theme/Icons';

const { getDefaultOption } = fieldHelper;
const { getDefaultRow, getDefaultTextColumn, getDefaultDropdownColumn } = tableHelper;

/** Legacy Rich Text Field Example
{
  title: 'Rich',
  type: FieldTypes.richText,
  icon: ThemeIcons.fields.richText,
  displayType: FieldDisplayTypes.original,
  description: 'Logo or graphic'
}
*/

/**
 * Uncomment below to use custom Icon for fields
 * 1. Custom icon can be a svg or image url
{
  title: 'Custom Icon SVG ',
  type: FieldTypes.text,
  iconType:"custom",
  icon: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      width={25}
      height={25}
      fill={'green'}
    >
      <circle cx="50" cy="50" r="40" />
      <line x1="50" y1="10" x2="50" y2="90" stroke="white" strokeWidth="5" />
      <line x1="10" y1="50" x2="90" y2="50" stroke="white" strokeWidth="5" />
    </svg>
  ),
  displayType: FieldDisplayTypes.original,
},
{
  title: 'Custom Icon Url',
  type: FieldTypes.text,
  iconType:"url",
  icon:'https://picsum.photos/200/300',
  displayType: FieldDisplayTypes.original,
},
**/

const image = {
  title: 'Image',
  type: FieldTypes.image,
  icon: ThemeIcons.fields.image,
  displayType: FieldDisplayTypes.original,
};

const file = {
  title: 'File',
  type: FieldTypes.file,
  icon: ThemeIcons.fields.file,
  displayType: FieldDisplayTypes.original,
};
const headingText = {
  title: 'Heading Text',
  type: FieldTypes.block,
  icon: ThemeIcons.fields.blockHeading,
  displayType: FieldDisplayTypes.original,
  value: 'Heading',
  fontSize: 28,
  fontWeight: FormStylesOptions.fontWeightBold,
};
const displayText = {
  title: 'Display Text',
  type: FieldTypes.block,
  icon: ThemeIcons.fields.blockParagraph,
  displayType: FieldDisplayTypes.original,
  value: 'Display text',
};

const emptySpace = {
  title: 'Empty Space',
  type: FieldTypes.block,
  icon: ThemeIcons.fields.blockEmptyspace,
  displayType: FieldDisplayTypes.original,
  borderColor: 'transparent',
  backgroundColor: 'transparent',
};

const text = {
  title: 'Text',
  type: FieldTypes.text,
  icon: ThemeIcons.fields.text,
  displayType: FieldDisplayTypes.original,
};

const multilineText = {
  title: 'Multiline Text',
  type: FieldTypes.textarea,
  icon: ThemeIcons.fields.textarea,
  displayType: FieldDisplayTypes.original,
};

const number = {
  title: 'Number',
  type: FieldTypes.number,
  icon: ThemeIcons.fields.number,
  displayType: FieldDisplayTypes.original,
};

const dateTime = {
  title: 'Date Time',
  type: FieldTypes.date,
  icon: ThemeIcons.fields.date,
  displayType: FieldDisplayTypes.original,
  format: 'MM/DD/YYYY hh:mma'
};

const dropdown = {
  title: 'Dropdown',
  type: FieldTypes.dropdown,
  icon: ThemeIcons.fields.dropdown,
  displayType: FieldDisplayTypes.original,
  options: [getDefaultOption('Yes'), getDefaultOption('No'), getDefaultOption('N/A')],
};

const multiSelect = {
  title: 'Multiple Choice',
  type: FieldTypes.multiSelect,
  icon: ThemeIcons.fields.multiSelect,
  multi: true,
  displayType: FieldDisplayTypes.original,
  options: [getDefaultOption('Yes'), getDefaultOption('No'), getDefaultOption('N/A')],
};

const singleChoice = {
  title: 'Single Choice',
  type: FieldTypes.multiSelect,
  icon: ThemeIcons.fields.multiSelectSingle,
  multi: false,
  displayType: FieldDisplayTypes.original,
  options: [getDefaultOption('Yes'), getDefaultOption('No'), getDefaultOption('N/A')],
};

const checkbox = {
  title: 'Checkbox',
  type: FieldTypes.multiSelect,
  icon: ThemeIcons.fields.selector,
  displayType: FieldDisplayTypes.check,
  options: [getDefaultOption('Option 1')],
};

const signature = {
  title: 'Signature',
  type: FieldTypes.signature,
  icon: ThemeIcons.fields.signature,
  displayType: FieldDisplayTypes.original,
};

const table = {
  title: 'Table',
  type: FieldTypes.table,
  icon: ThemeIcons.fields.table,
  displayType: FieldDisplayTypes.original,
  tableColumns: [
    getDefaultTextColumn({ width: 0 }), //0 or undefined indicates auto width (responsive)
    getDefaultDropdownColumn({ width: 0 }), //0 or undefined indicates auto width (responsive)
    getDefaultTextColumn({ width: 0 }), //0 or undefined indicates auto width (responsive)
  ],
  value: [getDefaultRow(), getDefaultRow(), getDefaultRow()],
};

const chart = {
  title: 'Chart',
  type: FieldTypes.chart,
  icon: ThemeIcons.fields.chart,
  displayType: FieldDisplayTypes.original,
  primaryDisplayOnly: true,
  yTitle: 'Vertical',
  yMax: 100,
  yMin: 0,
  xTitle: 'Horizontal',
  xMax: 100,
  xMin: 0,
};

const collection = {
  title: 'Collection',
  type: FieldTypes.collection,
  icon: ThemeIcons.fields.collection,
  displayType: FieldDisplayTypes.original,
  value: [],
  schema: DefaultCollectionSchema,
};

const rte = {
  title: 'Rich Text',
  type: FieldTypes.rte,
  icon: ThemeIcons.fields.richText,
  displayType: FieldDisplayTypes.original,
};

const pricingTable = {
  title: 'Pricing Table',
  type: 'pricingTable',
  icon: ThemeIcons.fields.pricingTable,
  displayType: 'original',
  value: {
    columns: [
      {
        _id: generateObjectId(),
        label: 'Feature',
        width: '25%',
        type: 'description',
        hidden: false,
        cells: [
          {}
        ]
      },
      {
        _id: generateObjectId(),
        label: 'Price',
        width: '15%',
        type: 'price',
        hidden: false,
        cells: [
          {}
        ]
      },
      {
        _id: generateObjectId(),
        label: 'Quantity',
        width: '15%',
        type: 'quantity',
        hidden: false,
        cells: [
          {}
        ]
      },
      {
        _id: generateObjectId(),
        label: 'Discount',
        width: '15%',
        type: 'discount',
        format: 'percent',
        hidden: false,
        cells: [
          {}
        ]
      },
      {
        _id: generateObjectId(),
        label: 'Tax',
        width: '15%',
        type: 'tax',
        format: 'percent',
        hidden: false,
        cells: [
          {}
        ]
      },
      {
        _id: generateObjectId(),
        label: 'Subtotal',
        width: '15%',
        type: 'subtotal',
        hidden: false,
        cells: [
          {}
        ]
      }
    ],
    summaryColumns: [
      {
        _id: generateObjectId(),
        label: 'Subtotal',
        type: 'subtotal',
        format: 'amount'
      },
      {
        _id: generateObjectId(),
        label: 'Tax',
        type: 'tax',
        format: 'percent'
      },
      {
        _id: generateObjectId(),
        label: 'Fee',
        type: 'fee',
        format: 'amount'
      },
      {
        _id: generateObjectId(),
        label: 'Discount',
        type: 'discount',
        format: 'amount'
      },
      {
        _id: generateObjectId(),
        label: 'Total',
        type: 'total',
        format: 'amount'
      }
    ]
  }
};

export const fieldOptions = {
  image,
  file,
  headingText,
  displayText,
  emptySpace,
  text,
  multilineText,
  number,
  dateTime,
  dropdown,
  multiSelect,
  singleChoice,
  checkbox,
  signature,
  table,
  chart,
  rte,
  pricingTable,
  //Disabling collections for now and will be added later depending on
  //if ST wants exclusivity or not.
  collection,
};

export default [
  image,
  file,
  headingText,
  displayText,
  emptySpace,
  text,
  multilineText,
  number,
  dateTime,
  dropdown,
  multiSelect,
  singleChoice,
  checkbox,
  signature,
  table,
  chart,
];
